import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import { X, Hash } from "lucide-react";

const modalStyles = {
  overlay: {
    backgroundColor: "rgba(15, 23, 42, 0.55)",
    zIndex: 9999,
  },
  content: {
    inset: "auto",
    padding: 0,
    border: "none",
    background: "transparent",
    overflow: "visible",
  },
};

const toStr = (v) => (v === undefined || v === null ? "" : String(v));

const CODE_PREFIX = "ALM";
const CODE_KEY = "sucursales_nextCodeNumber_v1";

const pad3 = (n) => String(n).padStart(3, "0");

const getNextNumber = () => {
  const raw = localStorage.getItem(CODE_KEY);
  const n = Number(raw || 1);
  return Number.isFinite(n) && n > 0 ? n : 1;
};

const peekNextCode = () => {
  const n = getNextNumber();
  return `${CODE_PREFIX}-${pad3(n)}`;
};

const commitNextCode = () => {
  const n = getNextNumber();
  localStorage.setItem(CODE_KEY, String(n + 1));
};

const buildDireccionCompleta = (form) => {
  const partes = [];

  const calle = toStr(form.calle).trim();
  const numExterior = toStr(form.numExterior).trim();
  const numInterior = toStr(form.numInterior).trim();
  const colonia = toStr(form.colonia).trim();
  const municipio = toStr(form.municipio).trim();
  const estadoDireccion = toStr(form.estadoDireccion).trim();
  const cp = toStr(form.cp).trim();
  const pais = toStr(form.pais).trim();

  if (calle) {
    let linea = calle;

    if (numExterior) linea += ` ${numExterior}`;
    if (numInterior) linea += ` Int. ${numInterior}`;

    partes.push(linea);
  }

  if (colonia) partes.push(colonia);
  if (municipio) partes.push(municipio);
  if (estadoDireccion) partes.push(estadoDireccion);
  if (cp) partes.push(`C.P. ${cp}`);
  if (pais) partes.push(pais);

  return partes.join(", ");
};

export default function ModalNuevaSucursal({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  primaryColor = "#3B82F6",
}) {
  const [saving, setSaving] = useState(false);

  const defaults = useMemo(
    () => ({
      codigo: "",
      nombre: "",
      tipo: "secondary",

      calle: "",
      numExterior: "",
      numInterior: "",
      cp: "",
      colonia: "",
      municipio: "",
      estadoDireccion: "",
      pais: "México",

      direccion: "",
      responsable: "",
      telefono: "",
      correo: "",
      ...(initialData || {}),
    }),
    [initialData],
  );

  const [form, setForm] = useState(defaults);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setForm(defaults);
      setErrors({});
      setSaving(false);
    }
  }, [isOpen, defaults]);

  const setField = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setErrors((prev) => ({ ...prev, [k]: "" }));
  };

  const validate = () => {
    const e = {};
    const msg = "Este campo es requerido";

    if (!toStr(form.codigo).trim()) e.codigo = msg;
    if (!toStr(form.nombre).trim()) e.nombre = msg;
    if (!toStr(form.calle).trim()) e.calle = msg;

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleAutoAsignar = () => {
    setField("codigo", peekNextCode());
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;

    try {
      setSaving(true);

      const direccionCompleta = buildDireccionCompleta(form);

      const payload = {
        codigo: toStr(form.codigo).trim(),
        nombre: toStr(form.nombre).trim(),
        tipo: form.tipo === "main" ? "Principal" : "Secundario",

        calle: toStr(form.calle).trim(),
        numExterior: toStr(form.numExterior).trim(),
        numInterior: toStr(form.numInterior).trim(),
        cp: toStr(form.cp).trim(),
        colonia: toStr(form.colonia).trim(),
        municipio: toStr(form.municipio).trim(),
        estadoDireccion: toStr(form.estadoDireccion).trim(),
        pais: toStr(form.pais).trim(),

        direccion: direccionCompleta,
        responsable: toStr(form.responsable).trim(),
        telefono: toStr(form.telefono).trim(),
        correo: toStr(form.correo).trim(),
      };

      await onSubmit?.(payload);
      commitNextCode();
      onClose?.();
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    setErrors({});
    onClose?.();
  };

  return (
    <>
      <style>{`
        :root{
          --ns-primary: ${primaryColor};
          --ns-text: #0f172a;
          --ns-muted: #64748b;
          --ns-border: #E5E7EB;
          --ns-bg: #ffffff;
          --ns-soft: #F3F4F6;
          --ns-shadow: 0 22px 60px rgba(2,6,23,.28);
          --ns-radius: 18px;
          --ns-radius-input: 14px;
        }

        .ns-card,
        .ns-card *{
          box-sizing: border-box;
        }

        .ns-modal-wrap{
          position: fixed;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 12px;
        }

        .ns-card{
          width: min(760px, calc(100vw - 24px));
          background: var(--ns-bg);
          border-radius: var(--ns-radius);
          box-shadow: var(--ns-shadow);
          overflow: hidden;
          border: 1px solid rgba(229,231,235,.95);
          font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, "Noto Sans", "Helvetica Neue", sans-serif;
          color: var(--ns-text);
        }

        .ns-inner{
          padding: 20px;
        }

        .ns-header{
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 20px;
          padding-bottom: 18px;
          border-bottom: 1px solid var(--ns-border);
        }

        .ns-title{
          margin: 0;
          font-size: 24px;
          line-height: 1.15;
          font-weight: 400;
          letter-spacing: -0.02em;
          color: var(--ns-text);
        }

        .ns-xbtn{
          border: none;
          background: transparent;
          padding: 8px;
          border-radius: 10px;
          cursor: pointer;
          color: #475569;
          transition: background .15s ease, color .15s ease, transform .15s ease;
        }
        .ns-xbtn:hover{ background: #F1F5F9; color: #0f172a; }
        .ns-xbtn:active{ transform: scale(.98); }
        .ns-xbtn:disabled{ opacity: .6; cursor: not-allowed; }

        .ns-form{
          padding-top: 0;
        }

        .ns-stack{
          display: flex;
          flex-direction: column;
          gap: 18px;
        }

        .ns-label{
          display: block;
          font-size: 14px;
          font-weight: 400;
          color: #334155;
          margin: 0 0 8px;
        }

        .ns-sublabel{
          display: block;
          font-size: 12px;
          font-weight: 400;
          color: #6B7280;
          margin: 0 0 6px;
        }

        .ns-required{
          color: #ef4444;
          margin-left: 6px;
          font-weight: 600;
        }

        .ns-input,
        .ns-select,
        .ns-textarea{
          width: 100%;
          max-width: 100%;
          height: 38px;
          border-radius: 12px;
          border: 1px solid var(--ns-border);
          background: #fff;
          padding: 8px 12px;
          font-size: 14px;
          line-height: 1.25;
          font-family: inherit;
          font-weight: 400;
          color: var(--ns-text);
          outline: none;
          transition: box-shadow .15s ease, border-color .15s ease;
        }

        .ns-input::placeholder,
        .ns-textarea::placeholder{
          color: #9CA3AF;
        }

        .ns-input:focus,
        .ns-select:focus,
        .ns-textarea:focus{
          outline: none;
          border-color: var(--ns-border);
          box-shadow: 0 0 0 2px var(--ns-primary);
        }

        .ns-input.ns-invalid,
        .ns-select.ns-invalid,
        .ns-textarea.ns-invalid{
          border-color: #ef4444;
          box-shadow: none;
        }

        .ns-input.ns-invalid:focus,
        .ns-select.ns-invalid:focus,
        .ns-textarea.ns-invalid:focus{
          border-color: #ef4444;
          box-shadow: none;
        }

        .ns-flex1{
          flex: 1;
          min-width: 0;
        }

        .ns-row{
          display: flex;
          gap: 12px;
          align-items: center;
          flex-wrap: wrap;
        }

        .ns-autobtn{
          height: 38px;
          padding: 0 16px;
          border-radius: 12px;
          border: none;
          background: var(--ns-primary);
          color: #fff;
          font-size: 14px;
          font-weight: 500;
          white-space: nowrap;
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          transition: all .15s ease;
          line-height: 1;
        }

        .ns-autobtn:hover{
          filter: brightness(.95);
        }

        .ns-autobtn:active{
          transform: translateY(1px);
        }

        .ns-autobtn:disabled{
          opacity: .65;
          cursor: not-allowed;
          filter: none;
          transform: none;
        }

        @media (max-width: 640px){
          .ns-autobtn{
            width: 100%;
            justify-content: center;
          }
        }

        .ns-grid2{
          display: grid;
          grid-template-columns: 1fr;
          gap: 18px;
        }

        @media (min-width: 768px){
          .ns-grid2{
            grid-template-columns: 1fr 1fr;
          }
        }

        .ns-grid4{
          display: grid;
          grid-template-columns: 1fr;
          gap: 12px;
        }

        @media (min-width: 768px){
          .ns-grid4{
            grid-template-columns: 2fr 1fr 1fr;
          }
        }

        .ns-grid-cp-colonia{
          display: grid;
          grid-template-columns: 1fr;
          gap: 12px;
        }

        @media (min-width: 768px){
          .ns-grid-cp-colonia{
            grid-template-columns: 1fr 2fr;
          }
        }

        .ns-error{
          margin-top: 8px;
          font-size: 13px;
          color: #ef4444;
        }

        .ns-footer{
          margin-top: 18px;
          padding-top: 18px;
          border-top: 1px solid var(--ns-border);
          display: flex;
          gap: 10px;
        }

        .ns-btn{
          flex: 1;
          height: 40px;
          padding: 0 16px;
          border-radius: 12px;
          border: none;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all .15s ease;
        }

        .ns-cancel{
          background: #F3F4F6;
          color: #334155;
        }
        .ns-cancel:hover{ background: #EDEFF3; }

        .ns-save{
          background: var(--ns-primary);
          color: #fff;
          box-shadow: none;
        }

        .ns-save:hover{
          filter: brightness(.95);
        }

        .ns-btn:active{ transform: translateY(1px); }
        .ns-btn:disabled{ opacity: .65; cursor: not-allowed; box-shadow: none; }

        .ns-select{
          appearance: none;
          background-image:
            linear-gradient(45deg, transparent 50%, #64748b 50%),
            linear-gradient(135deg, #64748b 50%, transparent 50%);
          background-position:
            calc(100% - 18px) 17px,
            calc(100% - 12px) 17px;
          background-size: 6px 6px, 6px 6px;
          background-repeat: no-repeat;
          padding-right: 38px;
        }
      `}</style>

      <Modal
        isOpen={!!isOpen}
        onRequestClose={handleClose}
        style={modalStyles}
        ariaHideApp={false}
        shouldCloseOnOverlayClick={!saving}
        shouldCloseOnEsc={!saving}
      >
        <div className="ns-modal-wrap" onMouseDown={handleClose}>
          <div
            className="ns-card"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="ns-inner">
              <div className="ns-header">
                <h2 className="ns-title">Nueva Sucursal</h2>
                <button
                  className="ns-xbtn"
                  onClick={handleClose}
                  disabled={saving}
                  aria-label="Cerrar"
                >
                  <X size={24} />
                </button>
              </div>

              <form className="ns-form" onSubmit={handleSubmit}>
                <div className="ns-stack">
                  <div>
                    <label className="ns-label">
                      Código <span className="ns-required">*</span>
                    </label>

                    <div className="ns-row">
                      <input
                        type="text"
                        className={`ns-input ns-flex1 ${
                          errors.codigo ? "ns-invalid" : ""
                        }`}
                        value={form.codigo}
                        onChange={(e) => setField("codigo", e.target.value)}
                      />

                      <button
                        type="button"
                        className="ns-autobtn"
                        onClick={handleAutoAsignar}
                        disabled={saving}
                        title="Auto-asignar"
                      >
                        <Hash size={16} />
                        <span className="hidden sm:inline">Auto-asignar</span>
                      </button>
                    </div>

                    {!!errors.codigo && (
                      <div className="ns-error">{errors.codigo}</div>
                    )}
                  </div>

                  <div className="ns-grid2">
                    <div>
                      <label className="ns-label">
                        Nombre <span className="ns-required">*</span>
                      </label>
                      <input
                        type="text"
                        className={`ns-input ${
                          errors.nombre ? "ns-invalid" : ""
                        }`}
                        value={form.nombre}
                        onChange={(e) => setField("nombre", e.target.value)}
                      />
                      {!!errors.nombre && (
                        <div className="ns-error">{errors.nombre}</div>
                      )}
                    </div>

                    <div>
                      <label className="ns-label">Tipo</label>
                      <select
                        className="ns-select"
                        value={form.tipo}
                        onChange={(e) => setField("tipo", e.target.value)}
                      >
                        <option value="main">Principal</option>
                        <option value="secondary">Secundario</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="ns-label">Dirección</label>

                    <div className="ns-stack">
                      <div className="ns-grid4">
                        <div>
                          <label className="ns-sublabel">
                            Calle <span className="ns-required">*</span>
                          </label>
                          <input
                            type="text"
                            className={`ns-input ${
                              errors.calle ? "ns-invalid" : ""
                            }`}
                            placeholder="Ej. Av. Insurgentes Sur"
                            value={form.calle}
                            onChange={(e) => setField("calle", e.target.value)}
                          />
                          {!!errors.calle && (
                            <div className="ns-error">{errors.calle}</div>
                          )}
                        </div>

                        <div>
                          <label className="ns-sublabel">Núm. Exterior</label>
                          <input
                            type="text"
                            className="ns-input"
                            placeholder="1234"
                            value={form.numExterior}
                            onChange={(e) =>
                              setField("numExterior", e.target.value)
                            }
                          />
                        </div>

                        <div>
                          <label className="ns-sublabel">Núm. Interior</label>
                          <input
                            type="text"
                            className="ns-input"
                            placeholder="4B"
                            value={form.numInterior}
                            onChange={(e) =>
                              setField("numInterior", e.target.value)
                            }
                          />
                        </div>
                      </div>

                      <div className="ns-grid-cp-colonia">
                        <div>
                          <label className="ns-sublabel">C.P.</label>
                          <input
                            type="text"
                            className="ns-input"
                            placeholder="00000"
                            value={form.cp}
                            onChange={(e) => setField("cp", e.target.value)}
                            maxLength={5}
                          />
                        </div>

                        <div>
                          <label className="ns-sublabel">Colonia</label>
                          <input
                            type="text"
                            className="ns-input"
                            placeholder="Ej. Del Valle"
                            value={form.colonia}
                            onChange={(e) =>
                              setField("colonia", e.target.value)
                            }
                          />
                        </div>
                      </div>

                      <div className="ns-grid2">
                        <div>
                          <label className="ns-sublabel">Municipio</label>
                          <input
                            type="text"
                            className="ns-input"
                            placeholder="Ej. Benito Juárez"
                            value={form.municipio}
                            onChange={(e) =>
                              setField("municipio", e.target.value)
                            }
                          />
                        </div>

                        <div>
                          <label className="ns-sublabel">Estado</label>
                          <select
                            className="ns-select"
                            value={form.estadoDireccion}
                            onChange={(e) =>
                              setField("estadoDireccion", e.target.value)
                            }
                          >
                            <option value="">Seleccionar estado</option>
                            <option value="Aguascalientes">
                              Aguascalientes
                            </option>
                            <option value="Baja California">
                              Baja California
                            </option>
                            <option value="Baja California Sur">
                              Baja California Sur
                            </option>
                            <option value="Campeche">Campeche</option>
                            <option value="Chiapas">Chiapas</option>
                            <option value="Chihuahua">Chihuahua</option>
                            <option value="Ciudad de México">
                              Ciudad de México
                            </option>
                            <option value="Coahuila">Coahuila</option>
                            <option value="Colima">Colima</option>
                            <option value="Durango">Durango</option>
                            <option value="Estado de México">
                              Estado de México
                            </option>
                            <option value="Guanajuato">Guanajuato</option>
                            <option value="Guerrero">Guerrero</option>
                            <option value="Hidalgo">Hidalgo</option>
                            <option value="Jalisco">Jalisco</option>
                            <option value="Michoacán">Michoacán</option>
                            <option value="Morelos">Morelos</option>
                            <option value="Nayarit">Nayarit</option>
                            <option value="Nuevo León">Nuevo León</option>
                            <option value="Oaxaca">Oaxaca</option>
                            <option value="Puebla">Puebla</option>
                            <option value="Querétaro">Querétaro</option>
                            <option value="Quintana Roo">Quintana Roo</option>
                            <option value="San Luis Potosí">
                              San Luis Potosí
                            </option>
                            <option value="Sinaloa">Sinaloa</option>
                            <option value="Sonora">Sonora</option>
                            <option value="Tabasco">Tabasco</option>
                            <option value="Tamaulipas">Tamaulipas</option>
                            <option value="Tlaxcala">Tlaxcala</option>
                            <option value="Veracruz">Veracruz</option>
                            <option value="Yucatán">Yucatán</option>
                            <option value="Zacatecas">Zacatecas</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="ns-sublabel">País</label>
                        <select
                          className="ns-select"
                          value={form.pais}
                          onChange={(e) => setField("pais", e.target.value)}
                        >
                          <option value="">Seleccionar país</option>
                          <option value="México">México</option>
                          <option value="Estados Unidos">Estados Unidos</option>
                          <option value="Canadá">Canadá</option>
                          <option value="Guatemala">Guatemala</option>
                          <option value="Colombia">Colombia</option>
                          <option value="Argentina">Argentina</option>
                          <option value="Chile">Chile</option>
                          <option value="Brasil">Brasil</option>
                          <option value="España">España</option>
                          <option value="Alemania">Alemania</option>
                          <option value="China">China</option>
                          <option value="Japón">Japón</option>
                          <option value="Otro">Otro</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="ns-grid2">
                    <div>
                      <label className="ns-label">Responsable</label>
                      <input
                        type="text"
                        className="ns-input"
                        value={form.responsable}
                        onChange={(e) =>
                          setField("responsable", e.target.value)
                        }
                      />
                    </div>

                    <div>
                      <label className="ns-label">Teléfono</label>
                      <input
                        type="tel"
                        className="ns-input"
                        value={form.telefono}
                        onChange={(e) => setField("telefono", e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="ns-label">Correo Electrónico</label>
                    <input
                      type="email"
                      className="ns-input"
                      value={form.correo}
                      onChange={(e) => setField("correo", e.target.value)}
                    />
                  </div>
                </div>

                <div className="ns-footer">
                  <button
                    type="button"
                    className="ns-btn ns-cancel"
                    onClick={handleClose}
                    disabled={saving}
                  >
                    Cancelar
                  </button>

                  <button
                    type="submit"
                    className="ns-btn ns-save"
                    disabled={saving}
                  >
                    {saving ? "Guardando..." : "Guardar"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
